/**
 * One scenario of the protocol (boards 1, 3 and 4): collapsed, its name, state, Ready and switch; open, how it does
 * on live tickets, how it is reached, its reply, its writes, and what it did since it went on auto-send.
 */

import { ChevronDown, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { PromotionType } from '@/modules/promotion/types';
import type { ScenarioTypeBranch } from '@/services/ai-function.service';
import type { AutomationEvidence, LiveType } from '../types';
import {
	formatWhen,
	isPromoted,
	readyBadgeOf,
	type ReplyJobState,
	scenarioLabelOf,
	type ScenarioState,
	seenLineOf,
	stateBadgeVariantOf,
	stateLabelOf
} from './scenarios';
import { WriteAction } from './write-action';

const Section = ({ title, children, muted }: { title: string; children: React.ReactNode; muted?: boolean }) => (
	<section className={cn('flex flex-col gap-1.5 border-t border-gray-300 px-3.5 py-3', muted && 'bg-gray-50')}>
		<span className='text-xs tracking-wide text-gray-800 uppercase'>{title}</span>
		{children}
	</section>
);

const Chip = ({ children }: { children: React.ReactNode }) => (
	<span className='rounded-md bg-gray-100 px-1.5 font-medium text-text-heading'>{children}</span>
);

/** What the scenario's replies did since it went on auto-send (board 4). */
export const AfterSwitchOn = ({ evidence, since }: { evidence: AutomationEvidence; since: string | null }) => (
	<Section title={since ? `Since Auto-send went on · ${formatWhen(since)}` : 'Since Auto-send went on'}>
		<dl className='grid grid-cols-4 gap-2 text-center'>
			{[
				['Sent by itself', evidence.sent],
				['Reviewed', `${evidence.reviewed} of ${evidence.sent}`],
				['Incorrect', evidence.incorrect],
				['Checks failed', evidence.checksFailed]
			].map(([label, value]) => (
				<div key={label} className='flex flex-col rounded-lg border border-gray-300 px-2 py-1.5'>
					<dt className='text-[11px] text-gray-800'>{label}</dt>
					<dd className='text-[15px] font-semibold text-text-heading tabular-nums'>{value}</dd>
				</div>
			))}
		</dl>
		{evidence.incorrectEntries.map((entry) => (
			<div key={`${entry.ticketId}-${entry.reviewedAt}`} className='flex flex-col rounded-lg border border-red-border px-3 py-2'>
				<span className='flex justify-between text-xs'>
					<span className='font-medium text-text-heading'>Ticket {entry.ticketId} · Incorrect</span>
					<span className='text-gray-800'>
						{entry.reviewerName ?? 'unknown'} · {formatWhen(entry.reviewedAt, true)}
					</span>
				</span>
				{entry.note && <span className='text-[13px] text-gray-900'>"{entry.note}"</span>}
			</div>
		))}
		<span className='text-xs text-gray-800'>
			{evidence.heldBack > 0 && `${evidence.heldBack} held back before sending. `}
			{evidence.sent - evidence.reviewed > 0 &&
				`${evidence.sent - evidence.reviewed} sent replies are not reviewed yet: an Incorrect review counts against Ready and toward the reject alert.`}
		</span>
	</Section>
);

/** How the scenario is reached, from its branch of the compiled protocol. */
const PathSection = ({ branch, outcome }: { branch: ScenarioTypeBranch | undefined; outcome: string }) => (
	<Section title='How this is reached' muted>
		{branch?.decisions.length ? (
			<ul className='flex flex-col gap-0.5 text-[13px] text-gray-900'>
				{branch.decisions.map((decision) => (
					<li key={decision.nodeId}>{decision.condition || decision.label}</li>
				))}
			</ul>
		) : (
			<span className='text-[13px] text-gray-900'>{outcome}</span>
		)}
		{Boolean(branch?.tools.length) && (
			<span className='flex flex-wrap items-center gap-1 text-[13px] text-gray-900'>
				{branch?.tools.map((tool, index) => (
					<span key={tool.nodeId} className='flex items-center gap-1'>
						{index > 0 && <span className='text-gray-800'>then</span>}
						<Chip>{tool.label || tool.tool}</Chip>
					</span>
				))}
			</span>
		)}
	</Section>
);

export interface ScenarioCardProps {
	sopId: string;
	type: LiveType;
	plan: PromotionType | undefined;
	branch: ScenarioTypeBranch | undefined;
	state: ScenarioState;
	/** Where the latest generation has this scenario's reply; null when it has nothing to say. */
	replyJob: ReplyJobState | null;
	/** Why it has no reply to promote; null when it has one. */
	noReplyReason: string | null;
	open: boolean;
	minTickets: number | null;
	/** When the active promotion went on, for the evidence since. */
	promotedAt: string | null;
	/** `typeId:tool` of writes a reject alert went out for. */
	alerted: string[];
	canEdit: boolean;
	busy: boolean;
	onToggle: () => void;
	onAutoSend: (on: boolean) => void;
	onPromote: () => void;
	onAutoApprove: (tool: string, on: boolean) => void;
	onReview: () => void;
}

export const ScenarioCard = ({
	sopId,
	type,
	plan,
	branch,
	state,
	replyJob,
	noReplyReason,
	open,
	minTickets,
	promotedAt,
	alerted,
	canEdit,
	busy,
	onToggle,
	onAutoSend,
	onPromote,
	onAutoApprove,
	onReview
}: ScenarioCardProps) => {
	const promoted = isPromoted(state);
	const label = scenarioLabelOf(type);
	const named = label !== type.description;
	const ready = type.ready.verdict === 'ready';
	const template = plan?.reply.templates[0]?.text ?? type.draft?.text ?? null;
	const labelOf = (tool: string): string => plan?.writes.find((write) => write.tool === tool)?.label ?? tool;
	return (
		<div className={cn('rounded-[14px] border bg-white', open ? 'border-gray-500 shadow-sm' : 'border-gray-300')}>
			<div className='flex items-center gap-2.5 px-3.5 py-2.5'>
				<button
					type='button'
					onClick={onToggle}
					aria-expanded={open}
					className='flex min-w-0 flex-1 cursor-pointer flex-col items-start text-left'
				>
					<span className='flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-text-heading'>
						<span className={cn(!open && 'line-clamp-2')}>{label}</span>
						<Badge variant={stateBadgeVariantOf(state, replyJob)}>
							{state === 'no_reply' && replyJob === 'writing' && <Loader2 aria-hidden />}
							{stateLabelOf(state, replyJob)}
						</Badge>
						<Badge variant={ready ? 'success' : 'warning'}>{readyBadgeOf(type.ready, minTickets)}</Badge>
						{type.compileChange && <Badge variant='outline'>{type.compileChange === 'added' ? 'New' : type.compileChange === 'updated' ? 'Updated' : 'Stale'}</Badge>}
					</span>
					<span className='text-xs text-gray-800'>{seenLineOf(type)}</span>
					{/* The badge already says a reply is being written or waiting; the line explains only what it can't. */}
					{state === 'no_reply' && noReplyReason && !open && replyJob !== 'writing' && replyJob !== 'waiting' && (
						<span
							className={cn(
								'line-clamp-2 text-xs',
								replyJob === 'failed' ? 'text-red-foreground' : 'text-yellow-foreground'
							)}
						>
							{noReplyReason}
						</span>
					)}
				</button>
				{promoted ? (
					<Switch
						checked={state === 'auto'}
						disabled={!canEdit || busy}
						onCheckedChange={onAutoSend}
						aria-label={`Auto-send ${label}`}
					/>
				) : (
					<Button
						variant='outline'
						size='sm'
						disabled={!canEdit || busy || state !== 'waiting'}
						title={state === 'no_reply' ? (noReplyReason ?? undefined) : undefined}
						onClick={onPromote}
					>
						Promote
					</Button>
				)}
				<ChevronDown
					className={cn('size-4 shrink-0 text-gray-700 transition-transform', open && 'rotate-180')}
					aria-hidden
				/>
			</div>
			{open && (
				<>
					{named && (
						<Section title='Scenario'>
							<p className='text-[13px] text-gray-900'>{type.description}</p>
						</Section>
					)}
					<PathSection branch={branch} outcome={type.outcome} />
					<Section title='Reply'>
						{template ? (
							<p className='text-[13px] whitespace-pre-line text-text-heading [overflow-wrap:anywhere]'>{template}</p>
						) : (
							<span className='text-[13px] text-yellow-foreground'>{noReplyReason ?? 'No reply written yet.'}</span>
						)}
						<span className='text-xs text-gray-800'>
							{state === 'auto'
								? 'Sends by itself.'
								: state === 'draft'
									? 'Kim drafts and a person sends. Switch Auto-send on to send it by itself.'
									: 'Promote adds it to the protocol as a Draft.'}{' '}
							Written from the scenario's tickets graded Perfect or Good Enough.
						</span>
						{type.tickets > 0 && (
							<Button variant='link' size='sm' className='h-auto self-start p-0 text-xs' onClick={onReview}>
								{type.tickets === 1 ? 'Review its live ticket' : `Review its ${type.tickets} live tickets`}
							</Button>
						)}
					</Section>
					{type.writes.length > 0 && (
						<Section title={type.writes.length > 1 ? 'Write actions' : 'Write action'}>
							{type.writes.map((write) => (
								<WriteAction
									key={write.tool}
									sopId={sopId}
									typeId={type.typeId}
									scenario={label}
									write={write}
									label={labelOf(write.tool)}
									alertSent={alerted.includes(`${type.typeId}:${write.tool}`)}
									blockedReason={state === 'auto' ? null : 'Auto-send the reply first: a write never runs while its reply waits for a person.'}
									canEdit={canEdit}
									busy={busy}
									onAutoApprove={(on) => onAutoApprove(write.tool, on)}
								/>
							))}
						</Section>
					)}
					{type.afterSwitchOn && <AfterSwitchOn evidence={type.afterSwitchOn} since={promotedAt} />}
				</>
			)}
		</div>
	);
};
